export const SYSTEM_TIME_ZONE = "America/Sao_Paulo";
export const SYSTEM_TIME_ZONE_LABEL = "UTC-3";
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};
const DEFAULT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function getSystemDateParts(date: Date) {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: SYSTEM_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  if (!year || !month || !day) {
    throw new Error("Não foi possível derivar a data no timezone do sistema.");
  }

  return { year, month, day };
}

export function buildDateOnly(year: number, monthIndex: number, day: number) {
  return `${year}-${pad(monthIndex + 1)}-${pad(day)}`;
}

export function formatDateOnly(date: Date) {
  const { year, month, day } = getSystemDateParts(date);
  return `${year}-${month}-${day}`;
}

export function todayDateOnly() {
  return formatDateOnly(new Date());
}

export function startOfMonthDateOnly(year: number, monthIndex: number) {
  return buildDateOnly(year, monthIndex, 1);
}

export function endOfMonthDateOnly(year: number, monthIndex: number) {
  return buildDateOnly(year, monthIndex, new Date(year, monthIndex + 1, 0).getDate());
}

export function dateOnlyToSystemDate(value: string) {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) {
    return new Date(value);
  }

  const [, year, month, day] = match;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 15, 0, 0));
}

function resolveSystemDate(value: Date | string) {
  return typeof value === "string" ? dateOnlyToSystemDate(value) : value;
}

export function formatSystemDate(
  value: Date | string,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS,
  locale = "pt-BR",
) {
  const date = resolveSystemDate(value);
  return new Intl.DateTimeFormat(locale, {
    timeZone: SYSTEM_TIME_ZONE,
    ...options,
  }).format(date);
}

export function formatSystemTime(
  value: Date | string,
  options: Intl.DateTimeFormatOptions = DEFAULT_TIME_OPTIONS,
  locale = "pt-BR",
) {
  const date = resolveSystemDate(value);
  return new Intl.DateTimeFormat(locale, {
    timeZone: SYSTEM_TIME_ZONE,
    ...options,
  }).format(date);
}

export function formatSystemDateTime(value: Date | string, locale = "pt-BR") {
  if (typeof value === "string" && DATE_ONLY_PATTERN.test(value)) {
    return formatSystemDate(value, DEFAULT_DATE_OPTIONS, locale);
  }

  const date = resolveSystemDate(value);
  return `${formatSystemDate(date, DEFAULT_DATE_OPTIONS, locale)} ${formatSystemTime(date, DEFAULT_TIME_OPTIONS, locale)}`;
}

export function formatSystemRelativeTime(value: Date | string, nowValue = new Date()) {
  const date = resolveSystemDate(value);
  const diffMs = date.getTime() - nowValue.getTime();

  if (!Number.isFinite(diffMs)) {
    return formatSystemDateTime(date);
  }

  const future = diffMs > 0;
  const absMs = Math.abs(diffMs);

  if (absMs < 60_000) {
    return "agora";
  }

  if (absMs < 3_600_000) {
    const minutes = Math.max(1, Math.round(absMs / 60_000));
    return future ? `em ${minutes} min` : `há ${minutes} min`;
  }

  if (absMs < 86_400_000) {
    const hours = Math.max(1, Math.round(absMs / 3_600_000));
    return future ? `em ${hours} h` : `há ${hours} h`;
  }

  if (absMs < 604_800_000) {
    const days = Math.max(1, Math.round(absMs / 86_400_000));
    return future ? `em ${days} d` : `há ${days} d`;
  }

  return formatSystemDate(date);
}

export function formatSystemMonthLabel(year: number, monthIndex: number) {
  return formatSystemDate(buildDateOnly(year, monthIndex, 1), {
    month: "long",
    year: "numeric",
  });
}

export function formatSystemMonthShortLabel(year: number, monthIndex: number) {
  return formatSystemDate(buildDateOnly(year, monthIndex, 1), {
    month: "short",
  }).replace(".", "");
}
